import { HfsSettingsActionEnum } from 'fleet-data-model';
import type { AppDispatch } from '~/store/Store';
import type { AppState } from '~/reducers';
import type { AssetListItem } from '~/data/AssetListItem/AssetListItem';
import type { DeepPartial } from '~/types/helper.types';
import { partialUpdateAsset } from '~/ducks/asset-updates/actions';
import { OperationStatus } from '~/domain/assets/models/OperationStatus';
import type { HfsOperationStateReflectionPayload } from '~/domain/assets/models/HfsOperationStateReflectionPayload';

// Keep in sync with use-gateway-fixed-location's isCompleted — the hook treats only
// these three as a successful save, so the optimistic reflection must match it.
const SUCCESS_STATUSES = new Set<OperationStatus>([
  OperationStatus.Done,
  OperationStatus.Completed,
  OperationStatus.Success,
]);

/**
 * Optimistically reflects a completed HAPP_FIXED_POSITION save on the asset so the
 * Set Location label (fixed/mobile) updates without waiting for a details refetch.
 *
 * The WS payload carries no enabled value, so the requested flag is read from the
 * operation registered at save time (see use-gateway-fixed-location saveFixedLocation).
 */
export const reflectFixedLocationOperationStatusUpdate =
  (payload: HfsOperationStateReflectionPayload) =>
  (dispatch: AppDispatch, getState: () => AppState) => {
    const { operationId, status } = payload;
    if (!SUCCESS_STATUSES.has(status)) {
      return;
    }

    const operation = getState().assetHfsOperations.operations.find(
      (item) =>
        item.operationId === operationId &&
        item.hfsActionId === HfsSettingsActionEnum.HAPP_FIXED_POSITION
    );
    if (operation === undefined) {
      return;
    }

    const args = operation.hfsActionArgs ?? {};
    const requestedEnabled = args.enabled;
    if (typeof requestedEnabled !== 'boolean') {
      return;
    }

    // When enabling a fixed position the user also moves the pin, so reflect the new
    // coordinates registered at save time — otherwise the asset keeps stale ones.
    const fixedLocation: { enabled: boolean; latitude?: string; longitude?: string } = {
      enabled: requestedEnabled,
    };
    if (
      requestedEnabled &&
      typeof args.latitude === 'string' &&
      typeof args.longitude === 'string'
    ) {
      fixedLocation.latitude = args.latitude;
      fixedLocation.longitude = args.longitude;
    }

    dispatch(
      partialUpdateAsset({
        assetId: operation.iprId,
        // DeepPartial does not recurse through the optional FixedLocation, so cast the
        // patch; mergeDeep keeps any fields not provided here.
        asset: {
          machineData: { fixedLocation },
        } as DeepPartial<AssetListItem>,
        forceUpdate: false,
      })
    );
  };
